import React, { useState } from 'react';
import { isMissingXrayData } from 'helpers/helpers';
import { FullJoints, Xray } from 'models/XrayModels';
import { ENDPOINT_BASE_URL } from 'hooks/constants';
import { Patient } from 'models/PatientModels';
import { PRO } from 'models/SurveyModels';
import HttpService from 'services/httpService';
import { Appointment } from 'models/AppointmentModels';
import { Org } from 'models/OrgModels';
import { ReportPageContent } from 'components/organisms/ReportPage/ReportPageContent';
import { useDemoExamRoomData } from 'hooks/useDemoExamRoomData';
import { useSearchParams } from 'react-router-dom';

const DemoReportRoom: React.FC = () => {

    const [searchParams] = useSearchParams()
    const patientName = searchParams.get('patientName')

    const {data, isLoading} = useDemoExamRoomData(patientName);
  
    const demo_org : Org = {
        org_id: 'Demo Org',
        created_at: String(Date),
        updated_at: String(Date),
        use_pros: false,
        country: '',
        name: '',
        city: '',
        state: '',
        phone: undefined,
        contact_email: undefined,
    }
  
    const [pdfLoading, setPdfLoading] = useState<boolean>(false);
    const [pdfError, setPdfError] = useState<boolean>(false);
    const [pdfFile, setPdfFile] = useState<Blob | null>(null);
  
    type PdfRequestDataType = {
      [FJ in FullJoints]?: {
        pro?: PRO;
        xrays: Xray[];
        priority: number;
      }
    };
  
    // TODO: cache this response somewhere, probably with react query
    const generatePdf = async (
      org: Org | undefined,
      patient?: Patient | null,
      appointment?: Appointment | null,
      prosByJoints?: { [FJ in FullJoints]?: PRO },
      xraysByJoints?: { [FJ in FullJoints]?: Xray[] },
    ) => {
      try {
        if (
          !patient
          || !org
          || !appointment
          || !appointment.joints
          || !prosByJoints
          || !xraysByJoints
        ) throw new Error('Patient, PRO, or Xray data missing.');
  
        setPdfError(false);
  
        const joints = Object.entries(appointment.joints)
          .filter(([, rank]) => !!rank)
          .sort(([, aRank], [, bRank]) => aRank! - bRank!) as [FullJoints, number][];
  
        const jointData = joints.reduce((obj, [fullJoint, rank]) => {
          const xrays = xraysByJoints[fullJoint];
  
          if (!xrays) return {};
  
          obj[fullJoint] = {
            ...(prosByJoints[fullJoint] ? { pro: prosByJoints[fullJoint] } : null),
            xrays: xraysByJoints[fullJoint]!,
            priority: rank,
          };
  
          if (isMissingXrayData(appointment)) delete obj[fullJoint];
  
          return obj;
        }, {} as PdfRequestDataType);
  
        setPdfLoading(true);
        const respData = await HttpService.post<Blob>({
            url: `${ENDPOINT_BASE_URL}/demo/report`,
            data: { org, patient, joint_data: jointData, appointment_date: appointment.appointment_date, user_name: "Demo User" },
            token: null,
            responseType: 'blob'
        });
        setPdfLoading(false);
        setPdfFile(respData.data);
      } catch (e) {
        console.error(e);
        setPdfError(true);
        setPdfLoading(false);
      }
    };

    return(<ReportPageContent 
        org={demo_org}
        patient={data?.patient}
        appointment={data?.appointment}
        prosByJoints={data?.latestProsMappedToJoints}
        xraysByJoints={data?.xraysMappedToJoints}
        generatePdf={generatePdf}
        flow={"DEMO"}
        isLoading={isLoading}
        pdfFile={pdfFile}
        pdfError={pdfError}
        pdfLoading={pdfLoading}
    />)

  };
  

export default DemoReportRoom;